/**
 * Source-typed activity trace (§17 element 1): turn a tool-call trace block into
 * a human "Searching email… · Querying DealCloud… · Reading cert workbook" line
 * with the matching source icon. Pure + presentational — the streaming trace
 * events already exist; this only re-labels a TOOL_CALL by the source it touches.
 */

import {
	Calculator,
	Database,
	FileSpreadsheet,
	FileText,
	Globe,
	type LucideIcon,
	Mail,
	MessageSquare,
	Search,
} from "lucide-react";

export interface ToolActivity {
	Icon: LucideIcon;
	phrase: string;
}

function str(value: unknown): string {
	return typeof value === "string" ? value : "";
}

/**
 * Describe the source-typed activity for a tool call, or `null` when the tool
 * has no natural source phrasing (the caller then keeps the raw tool label).
 */
export function describeToolActivity(
	tool: string | undefined,
	args: Record<string, unknown> | null | undefined,
): ToolActivity | null {
	const a = args ?? {};
	switch (tool) {
		case "query_structured": {
			const source = str(a.source).toLowerCase();
			if (source.startsWith("tape")) {
				return { Icon: Database, phrase: "Querying the loan tape" };
			}
			if (source === "domain_model") {
				return { Icon: Database, phrase: "Querying the domain model" };
			}
			if (source === "dealcloud") {
				return { Icon: Database, phrase: "Querying DealCloud" };
			}
			return { Icon: Database, phrase: "Querying structured data" };
		}
		case "search_catalog":
		case "analyze_document":
		case "analyze_parallel": {
			const system = str(a.source_system).toLowerCase();
			if (system === "email") return { Icon: Mail, phrase: "Searching email" };
			if (system === "slack")
				return { Icon: MessageSquare, phrase: "Reading Slack" };
			return { Icon: FileText, phrase: "Reading documents" };
		}
		case "manipulate_workbook":
			return { Icon: FileSpreadsheet, phrase: "Reading the workbook" };
		case "web_search":
			return { Icon: Globe, phrase: "Searching the web" };
		case "compute":
			return { Icon: Calculator, phrase: "Computing" };
		default:
			if (!tool) return null;
			return { Icon: Search, phrase: tool };
	}
}
